import {
	Button,
	CircularProgress,
	Divider,
	MuiAlert,
	Paper,
	ResponsiveDrawer,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@fox/atoms';
import { formatCurrencyFromMajorUnits } from '@fox/finance';
import { FlexColumnSpaced, Padded } from '@fox/layout';
import moment from 'moment';
import { useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { useInvoice } from '../../../Membership/query-hooks/query-implementations';
import { InvoiceStatus, SwiftFoxInvoice } from '../contracts';
import {
	dateFormat,
	dateTimeFormat,
	DrawerLine,
	EMPTY_FIELD_PLACEHOLDER,
	formatCurrency,
	InvoiceStatusChip,
	TABLE_BUTTON_STYLE
} from '../shared';
import { InvoicePdfButton } from './invoice-pdf';

export function InvoiceDetailedDesktop({ record }: { record: SwiftFoxInvoice }) {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Button variant="secondary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				View details
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title="Invoice details"
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
				}}
			>
				<InvoiceDetails record={record} />
			</ResponsiveDrawer>
		</>
	);
}

export function InvoceDetailedMobile({ record }: { record: SwiftFoxInvoice }) {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Button variant="secondary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				View details
			</Button>

			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Invoice details"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					setOpen(false);
				}}
			>
				<InvoiceDetails record={record} />
			</ResponsiveDrawer>
		</>
	);
}

export function InvoiceDetails({ record }: { record: SwiftFoxInvoice }) {
	const { contentId, requestToken } = useContentContext();
	const { currencies } = useFinancialContext();

	const {
		error,
		loading,
		data: invoice,
		refetch
	} = useInvoice({
		contentId,
		requestToken,
		invoiceId: record.id,
		skip: !record.id
	});

	if (loading) return <CircularProgress />;
	if (error) {
		return <MuiAlert severity="error">{error}</MuiAlert>;
	}
	if (!invoice) return <></>;
	const currency = invoice.currency ?? currencies.default;
	return (
		<Padded>
			<FlexColumnSpaced>
				<DrawerLine
					title={'Created Date: '}
					children={invoice.createdOn ? moment.utc(invoice.createdOn).local().format(dateTimeFormat) : EMPTY_FIELD_PLACEHOLDER}
				/>
				<DrawerLine title={'Invoice Number: '} children={invoice.integrationInvoiceNumber ?? invoice.id} />
				<DrawerLine title={'Invoice Reference: '} children={invoice.reference ?? EMPTY_FIELD_PLACEHOLDER} />
				<DrawerLine
					title={'Due Date: '}
					children={invoice.due ? moment.utc(invoice.due).local().format(dateFormat) : EMPTY_FIELD_PLACEHOLDER}
				/>
				<DrawerLine title={'Status: '} children={<InvoiceStatusChip status={invoice.status} />} />
				{invoice.subscription && invoice.period && (
					<DrawerLine
						title={'Subscription: '}
						children={`${invoice.subscription.fee.plan.name} (Started: ${invoice.period.subPeriodStart})`}
					/>
				)}
				<Divider />
				{invoice.lineItems.length > 0 ? (
					<TableContainer component={Paper}>
						<Table aria-label="line-items-table">
							<TableHead>
								<TableRow>
									<TableCell>Description</TableCell>
									<TableCell align="right">Qty</TableCell>
									<TableCell align="right">Unit</TableCell>
									<TableCell align="right">Amount</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{invoice.lineItems.map((item, index) => (
									<TableRow key={`${item.id}_${index}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
										<TableCell style={{ maxWidth: '10rem' }}>{item.comment}</TableCell>
										<TableCell align="right">{item.quantity}</TableCell>
										<TableCell align="right">
											{formatCurrencyFromMajorUnits(item.unitAmount, currency, {
												allowFractionalCurrency: true,
												cutOffCents: false
											})}
										</TableCell>
										<TableCell align="right">{formatCurrency(item.amount, currency)}</TableCell>
									</TableRow>
								))}
								<TableRow>
									<TableCell rowSpan={3} />
									<TableCell colSpan={2}>Subtotal</TableCell>
									<TableCell align="right">{formatCurrency(invoice.totalAmountPreTax, currency)}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={2}>Tax</TableCell>
									<TableCell align="right">{formatCurrency(invoice.totalTaxAmount, currency)}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={2}>Total</TableCell>
									<TableCell align="right">{formatCurrency(invoice.totalAmount, currency)}</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				) : null}
				{invoice.status === InvoiceStatus.AUTHORISED ||
				invoice.status === InvoiceStatus.PAID ||
				invoice.status === InvoiceStatus.PART_PAID ? (
					<InvoicePdfButton invoiceId={invoice.id} />
				) : null}
			</FlexColumnSpaced>
		</Padded>
	);
}
